import { Button, InputNumber, Select, Space } from 'antd';
import { ReactNode, useState } from 'react';

import { ShiftListItemDto, StipendDto, StipendType } from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';

import { useSelection } from '../../../hooks/use-selection';
import { useToast } from '../../../hooks/use-toast';

const STIPEND_TYPE_OPTIONS = [
    { value: StipendType.Meals, label: 'Meals' },
    { value: StipendType.Lodging, label: 'Lodging' },
    { value: StipendType.Incidentals, label: 'Incidentals' },
    { value: StipendType.Travel, label: 'Travel' },
];

interface AddStipendFormProps {
    onCancel: () => void;
    onSuccess: () => void;
}

const AddStipendForm = ({ onCancel, onSuccess }: AddStipendFormProps) => {
    const api = useApiClient();
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const { notification } = useToast();
    const [stipendType, setStipendType] = useState<StipendType | undefined>();
    const [amount, setAmount] = useState<number | null>(null);
    const [distance, setDistance] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const handleAdd = async () => {
        if (!stipendType || !amount || distance === null) {
            return;
        }

        setLoading(true);
        try {
            const updatedShift = await api?.stipendControllerAddStipend({
                shiftId: selection.id,
                stipendAddDto: {
                    stipendType,
                    amount: Math.round(amount * 100),
                    stipendMinimumDistance: distance,
                },
            });

            if (updatedShift?.stipends) {
                setSelection({
                    ...selection,
                    stipends: updatedShift.stipends,
                    stipendMinimumDistance: updatedShift.stipendMinimumDistance,
                });
            }

            notification?.success({ message: 'Stipend added' });
            onSuccess();
        } catch (error) {
            notification?.error({
                message: 'Failed to add stipend',
                description: unwrapError(error),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Space direction="vertical" size="small">
            <Space size="small">
                <Select
                    placeholder="Type"
                    style={{ width: 120 }}
                    options={STIPEND_TYPE_OPTIONS}
                    value={stipendType}
                    onChange={setStipendType}
                />
                <InputNumber
                    placeholder="Amount"
                    prefix="$"
                    style={{ width: 100 }}
                    min={1}
                    value={amount}
                    onChange={setAmount}
                />
                <InputNumber
                    placeholder="Distance (miles)"
                    style={{ width: 140 }}
                    min={0}
                    precision={0}
                    value={distance}
                    onChange={setDistance}
                />
            </Space>
            <Space size="small">
                <Button
                    type="primary"
                    disabled={
                        !stipendType || !amount || distance === null || loading
                    }
                    loading={loading}
                    onClick={() => void handleAdd()}
                >
                    Add
                </Button>
                <Button onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
            </Space>
        </Space>
    );
};

interface StipendItemProps {
    stipend: StipendDto;
    disabled: boolean;
}

const StipendItem = ({ stipend, disabled }: StipendItemProps) => {
    const api = useApiClient();
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const { notification } = useToast();
    const [loading, setLoading] = useState(false);

    const handleRemove = async () => {
        if (!confirm('Remove this stipend?')) {
            return;
        }

        setLoading(true);
        try {
            const updatedShift = await api?.stipendControllerRemoveStipend({
                shiftId: selection.id,
                stipendId: stipend.id,
            });

            if (updatedShift?.stipends) {
                setSelection({
                    ...selection,
                    stipends: updatedShift.stipends,
                });
            }

            notification?.success({ message: 'Stipend removed' });
        } catch (error) {
            notification?.error({
                message: 'Failed to remove stipend',
                description: unwrapError(error),
            });
        } finally {
            setLoading(false);
        }
    };

    const stipendTypeLabel = STIPEND_TYPE_OPTIONS.find(
        (opt) => opt.value === stipend.stipendType
    )?.label;

    return (
        <div style={{ textAlign: 'right' }}>
            {stipendTypeLabel} ${(stipend.amount / 100).toFixed(2)}
            {selection.stipendMinimumDistance != null &&
                ` (${selection.stipendMinimumDistance} mi)`}
            {!disabled && (
                <Button
                    danger
                    size="small"
                    style={{ marginLeft: 10 }}
                    onClick={() => void handleRemove()}
                    loading={loading}
                    disabled={loading}
                >
                    &times;
                </Button>
            )}
        </div>
    );
};

export const createStipendsFieldRenderer = (
    selection: ShiftListItemDto,
    isAddingStipend: boolean,
    onStartAdd: () => void,
    onCancelAdd: () => void
): (() => ReactNode) => {
    const renderStipends = () => {
        const stipends = selection.stipends ?? [];
        const isDisabled = !!selection.submittedTimeAt;

        return (
            <div>
                {stipends.length === 0 && !isAddingStipend && <div>None</div>}

                {stipends.map((stipend) => (
                    <StipendItem
                        key={stipend.id}
                        stipend={stipend}
                        disabled={isDisabled}
                    />
                ))}

                {isAddingStipend && (
                    <AddStipendForm
                        onCancel={onCancelAdd}
                        onSuccess={onCancelAdd}
                    />
                )}

                {!isAddingStipend && !isDisabled && (
                    <a onClick={onStartAdd}>Add stipend</a>
                )}
            </div>
        );
    };

    renderStipends.displayName = 'StipendsFieldRenderer';
    return renderStipends;
};
